import React from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Button } from '@plerionhq/design-system';
import { isChunkLoadError } from '../lib/chunk-reload';

import { Trans, useTranslation } from 'react-i18next';

const supportEmailLink = (subject: string, body: string) =>
  `mailto:support@plerion.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const ErrorFallback = ({ error }: FallbackProps) => {
  const { t } = useTranslation('containerInterface');
  console.error('Error Fallback', error);

  // A file failed to load and the auto-reload in bootstrap.tsx didn't fix it,
  // usually because the user's network can't reach our files.
  const isChunkError = isChunkLoadError(error);

  return (
    <div data-testid="error-boundary" role="alert">
      <h1 className="p-4 text-3xl leading-tight font-medium">
        {isChunkError ? t('partOfPlerionDidNotLoad') : t('error')}
      </h1>
      {isChunkError && (
        <p className="max-w-2xl px-4 text-base text-gray-600 dark:text-gray-300">
          <Trans
            t={t}
            i18nKey="browserCouldNotDownloadPartOfPlerion"
            components={{
              a: (
                <a
                  href={supportEmailLink(
                    t('partOfPlerionDidNotLoad'),
                    t('partOfPlerionDidNotLoadEmailBody', {
                      page: window.location.href,
                      time: new Date().toISOString(),
                      error:
                        error instanceof Error ? error.message : String(error),
                      interpolation: { escapeValue: false },
                    }),
                  )}
                  className="tertiary-action font-semibold"
                />
              ),
            }}
          />
        </p>
      )}
      <div className="p-4">
        <Button
          variant="primary"
          label={t('reload')}
          onClick={() => window.location.reload()}
          size="small"
        />
      </div>
    </div>
  );
};

export const withErrorBoundary = <T extends object>(
  Component: React.ComponentType<T>,
  fallbackComponent: React.ComponentType<FallbackProps> = ErrorFallback,
): React.ComponentType<T> => {
  return function ComponentWithErrorBoundary(props: T) {
    return (
      <ErrorBoundary FallbackComponent={fallbackComponent}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
};
